import * as yup from 'yup';
import { FormModal } from 'components/form/FormModal/FormModal';
import {
    FormAutocomplete,
    FormInputGroup,
    FormSelect,
    FormSubmitButton,
    FormText,
} from 'components/form';
import { CreatedBy } from 'components/common/CreatedBy/CreatedBy';
import { activateSnackbar } from 'store/features/ui';
import { Ticket } from 'types/User/firebaseTypes/ticket';
import styles from './IssueDetailsModal.module.scss';
import {
    convertDeliveryStatusToInternalName,
    deliveryStatusDisplayNames,
    ticketTriggerOptions,
    ticketTriggerToTroubleshooting,
} from 'lib/constants';
import { useEffect, useState } from 'react';
import { DefaultOpenModalProps } from 'components/button/OpenModalButton/OpenModalButton';
import { FieldValues, useFormContext } from 'react-hook-form';
import { RootState, useAppDispatch } from 'store';
import { getNewPortalUsers } from 'lib/firebase';
import { useSelector } from 'react-redux';
import { retrievedNewPortalUsers } from 'store/features/authentication';
import {
    useUpdateIssueTicketMutation,
    useResolveIssueTicketMutation,
    useUpdateResolvedIssueTicketMutation,
} from 'store/services/issue-tickets';
import Button from '@mui/material/Button';
import { Edit, Launch } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import SaveIcon from '@mui/icons-material/Save';
import { useNavigate } from 'react-router-dom';
import { Device } from 'types';
import { useLazyGetDeviceByLongIdNapiQuery } from 'store/services';
import { LoadingSpinner } from 'components/data/LoadingSpinner';
import Alert from '@mui/material/Alert/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Link from '@mui/material/Link';
import {
    OfflineLedSequences,
    ledSequenceOptions,
} from 'views/DeviceDetails/modals/OfflineLedSequences/OfflineLedSequences';
import { FormRadioOption } from 'components/form/FormRadio/FormRadio';
import { FormSelectOption } from 'components/form/types';

type IssueTicketInput =
    | Omit<Partial<Ticket>, 'taggedUsers'>
    | {
          taggedUsers?: string;
      };

const issueSchema = yup.object({
    name: yup.string().required(),
    description: yup.string().required(),
    taggedUsers: yup.string().nullable(),
    ticketTrigger: yup.string(),
    status: yup.string(),
    selectedLedSequence: yup.string().when('ticketTrigger', {
        is: (issueType: string) => issueType === 'offline',
        then: (schema) => schema.required(),
    }),
});

export interface IssueDetailsModalProps extends DefaultOpenModalProps {
    /** The issue ticket to display on the modal. */
    ticket: Ticket;
    /** A supplierId for the supplier that an admin user wants to view data for. */
    viewAsSupplier?: string;
    /** Callback to alert parent container that the issue has been edited. */
    onIssueUpdated?: React.Dispatch<React.SetStateAction<boolean>>;
    /** Callback to alert parent container to changes to the active ticket (which is displayed on the modal). */
    setActiveTicket?: React.Dispatch<React.SetStateAction<Ticket | null>>;
}

/** Shows editable details for a particular issue ticket. */
export const IssueDetailsModal = ({
    modalOpen,
    setModalOpen,
    ticket,
    viewAsSupplier,
    onIssueUpdated,
    setActiveTicket,
}: IssueDetailsModalProps) => {
    const defaults: IssueTicketInput = {
        name: ticket?.name ?? '',
        description: ticket?.description ?? '',
        taggedUsers: ticket?.taggedUsers?.[0]?.uid || '',
        ticketTrigger: ticket?.ticketTrigger ?? '',
        status: convertDeliveryStatusToInternalName[ticket?.status ?? ''],
    };

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const authentication = useSelector(
        (state: RootState) => state.authentication
    );

    const [getDeviceQuery] = useLazyGetDeviceByLongIdNapiQuery();

    const [device, setDevice] = useState<Device>();
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [defaultIssueValues, setDefaultIssueValues] =
        useState<IssueTicketInput>(defaults);

    const [updateIssueTicket] = useUpdateIssueTicketMutation();
    const [resolveIssueTicket] = useResolveIssueTicketMutation();
    const [updateResolvedIssueTicket] = useUpdateResolvedIssueTicketMutation();

    const userOptions = authentication.newPortalUsers
        ? Object.keys(authentication.newPortalUsers).map((key: string) => {
              return {
                  name: authentication.newPortalUsers?.[key]?.email || '',
                  value: authentication.newPortalUsers?.[key]?.uid || '',
              };
          })
        : [];

    const onClose = () => {
        setIsEdit(false);
        setModalOpen(false);
    };

    const onError = (error: FieldValues) => {
        const messages = Object.keys(error).map((key) => error[key]?.message);
        dispatch(
            activateSnackbar({
                message: JSON.stringify(messages),
                severity: 'error',
            })
        );
    };

    const onSubmit = (data: FieldValues) => {
        if (
            authentication?.supplierId &&
            authentication.newPortalUsers &&
            data &&
            ticket
        ) {
            setIsSaving(true);
            const selectedUser = data.taggedUsers
                ? authentication.newPortalUsers[data.taggedUsers]
                : null;

            const { selectedLedSequence, ticketTrigger, ...dataValues } = data;

            const ticketBody: Ticket = {
                ...ticket,
                ...{
                    ...dataValues,
                    ticketTrigger,
                    ticketCategory: 'issue',
                    offlineLedSequence:
                        ticketTrigger === 'offline'
                            ? selectedLedSequence
                            : undefined,
                },
            };

            if (selectedUser) ticketBody.taggedUsers = [selectedUser];

            const needsResolving =
                convertDeliveryStatusToInternalName[ticket.status] !== 'done' &&
                ticket.status === 'done';

            const updateRequest =
                ticket.status === 'done'
                    ? needsResolving
                        ? resolveIssueTicket
                        : updateResolvedIssueTicket
                    : updateIssueTicket;

            Promise.resolve(
                updateRequest({
                    supplierId: viewAsSupplier ?? authentication.supplierId,
                    deviceId: ticket.deviceId,
                    newTicket: ticketBody,
                }).then((result: any) => {
                    if (result && result.data) {
                        if (setActiveTicket) setActiveTicket(result.data);
                        if (onIssueUpdated) onIssueUpdated(true);
                        setIsSaving(false);
                        onClose();
                    }
                })
            );
        } else {
            dispatch(
                activateSnackbar({
                    message: 'Something went wrong, please try again.',
                    severity: 'error',
                })
            );
        }
    };

    const onFormModalClose = () => {
        if (setActiveTicket) setActiveTicket(null);
    };

    // Get device related to the issue and set default form values.
    useEffect(() => {
        if (ticket && modalOpen) {
            const issueFormValues = {
                name: ticket?.name ?? '',
                description: ticket?.description ?? '',
                taggedUsers: ticket?.taggedUsers?.[0]?.uid || '',
                ticketTrigger: ticket?.ticketTrigger ?? '',
                status: convertDeliveryStatusToInternalName[ticket?.status],
                selectedLedSequence: ticket?.offlineLedSequence,
            };

            setDefaultIssueValues(issueFormValues);

            getDeviceQuery({ longDeviceId: ticket.deviceId }).then((result) => {
                if (result.isSuccess && result.data) setDevice(result.data);
            });
        }
    }, [ticket, modalOpen, getDeviceQuery]);

    // Get data for taggedUsers.
    useEffect(() => {
        if (!!authentication.supplierId && !authentication.newPortalUsers) {
            getNewPortalUsers(authentication.supplierId).then(
                (newPortalUsers) => {
                    dispatch(retrievedNewPortalUsers(newPortalUsers));
                }
            );
        }
    }, [authentication, dispatch]);

    // Reset values when modal is opened/closed.
    useEffect(() => {
        setIsEdit(false);
        if (!modalOpen) {
            setDefaultIssueValues(defaults);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalOpen]);

    const ledOpts = [
        ...ledSequenceOptions,
        { value: 'unknown', name: 'Unknown or Not Available' },
    ];

    return (
        <div data-testid="IssueDetailsModal">
            {device ? (
                <FormModal
                    title={
                        <div className={styles.titleContainer}>
                            <div className={styles.title}>
                                Issue Ticket Details
                            </div>
                            <div>
                                Tank: {device.name ?? device.shortDeviceId}{' '}
                                <IconButton
                                    onClick={() => {
                                        onClose();
                                        navigate(
                                            `/device-details/${ticket.deviceId}`,
                                            {
                                                state: device,
                                            }
                                        );
                                    }}
                                    className={styles.linkButton}
                                >
                                    <Launch />
                                </IconButton>{' '}
                                | Customer:{' '}
                                {device.customer_name ||
                                    device.protected?.supplierId}
                            </div>
                        </div>
                    }
                    defaultValues={defaultIssueValues}
                    validationSchema={issueSchema}
                    onErrorHandler={onError}
                    modalOpen={modalOpen}
                    setModalOpen={setModalOpen}
                    onClose={onFormModalClose}
                    hideDefaultSubmit
                    actionChildren={
                        <div className={styles.modalActions}>
                            <div className={styles.metaData}>
                                <strong>Created By</strong>
                                <CreatedBy ticket={ticket} showIcon />
                            </div>
                            {!isEdit ? (
                                <Button
                                    disabled={ticket.status === 'done'}
                                    variant="contained"
                                    onClick={() => setIsEdit(true)}
                                    startIcon={<Edit />}
                                >
                                    Edit
                                </Button>
                            ) : (
                                <FormSubmitButton
                                    onErrorHandler={onError}
                                    onValidHandler={onSubmit}
                                    buttonLabel="Save"
                                    isLoading={isSaving}
                                    startIcon={<SaveIcon />}
                                    disabled={isSaving}
                                    fullWidth={false}
                                />
                            )}
                        </div>
                    }
                >
                    <IssueDetailsForm
                        isEdit={isEdit}
                        ticket={ticket}
                        ledOpts={ledOpts}
                        userOptions={userOptions}
                    />
                </FormModal>
            ) : (
                <LoadingSpinner />
            )}
        </div>
    );
};

interface IssueDetailsFormProps {
    isEdit: boolean;
    ticket: Ticket;
    ledOpts: FormRadioOption[];
    userOptions: FormSelectOption[];
}

const IssueDetailsForm = ({
    isEdit,
    ticket,
    ledOpts,
    userOptions,
}: IssueDetailsFormProps) => {
    const { watch } = useFormContext();

    const issueType = watch('ticketTrigger');

    return (
        <>
            <FormInputGroup>
                <FormText field="name" label="Ticket Name" disabled={!isEdit} />
                <FormSelect
                    label="Issue Type"
                    field="ticketTrigger"
                    options={ticketTriggerOptions}
                    disabled={!isEdit}
                />
            </FormInputGroup>
            <FormInputGroup>
                <FormText
                    field="description"
                    label="Description"
                    disabled={!isEdit}
                />
            </FormInputGroup>
            <FormInputGroup>
                {userOptions.length > 0 && (
                    <FormAutocomplete
                        label="Tagged User"
                        field="taggedUsers"
                        options={[{ name: '', value: '' }, ...userOptions]}
                        disabled={!isEdit}
                    />
                )}
                <FormSelect
                    label="Status"
                    field="status"
                    options={deliveryStatusDisplayNames.map((status) => ({
                        name: status,
                        value: convertDeliveryStatusToInternalName[status],
                    }))}
                    disabled={!isEdit}
                />
            </FormInputGroup>
            {isEdit && issueType === 'offline' && (
                <OfflineLedSequences hasUnknownOption />
            )}
            {!isEdit && !!ticketTriggerToTroubleshooting[ticket.ticketTrigger] && (
                <>
                    <Alert severity="info" sx={{ marginBottom: '12px' }}>
                        <AlertTitle>
                            {
                                ticketTriggerToTroubleshooting[
                                    ticket.ticketTrigger
                                ].title
                            }
                        </AlertTitle>
                        <span>
                            {
                                ticketTriggerToTroubleshooting[
                                    ticket.ticketTrigger
                                ].description
                            }
                        </span>

                        {ticketTriggerToTroubleshooting[ticket.ticketTrigger]
                            .supportDocsUrl && (
                            <span>
                                {' '}
                                For more information about this issue,{' '}
                                <Link
                                    href={
                                        ticketTriggerToTroubleshooting[
                                            ticket.ticketTrigger
                                        ].supportDocsUrl
                                    }
                                    target="__blank"
                                >
                                    see our support docs
                                </Link>
                                .
                            </span>
                        )}
                    </Alert>
                    {issueType === 'offline' && (
                        <div>
                            <strong>
                                Selected Troubleshooting Sequence:{' '}
                                {
                                    ledOpts.find(
                                        (seq) =>
                                            seq.value ===
                                            ticket.offlineLedSequence
                                    )?.name
                                }
                            </strong>
                            {
                                ledOpts.find(
                                    (seq) =>
                                        seq.value === ticket.offlineLedSequence
                                )?.afterChildren
                            }
                        </div>
                    )}
                </>
            )}
        </>
    );
};
